// =====================================================
// HOOK: useOrigins
// Description: Gestion des origines (canaux d'acquisition)
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import type { Origin, CreateOriginDto, UpdateOriginDto } from '../types/origin';
import { toast } from '../components/ui';

export function useOrigins() {
  const supabase = useSupabase();
  const [origins, setOrigins] = useState<Origin[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Récupère toutes les origines actives
   */
  const fetchOrigins = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('origins')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setOrigins(data || []);
    } catch (error) {
      console.error('Error fetching origins:', error);
      toast.error('Erreur lors du chargement des origines');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  /**
   * Crée une nouvelle origine
   */
  async function createOrigin(data: CreateOriginDto): Promise<Origin | null> {
    try {
      const { data: newOrigin, error } = await supabase
        .from('origins')
        .insert([
          {
            ...data,
            display_order: data.display_order ?? origins.length + 1,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setOrigins((prev) => [...prev, newOrigin].sort((a, b) => a.display_order - b.display_order));
      toast.success('Origine créée avec succès');
      return newOrigin;
    } catch (error) {
      console.error('Error creating origin:', error);
      toast.error("Erreur lors de la création de l'origine");
      return null;
    }
  }

  /**
   * Met à jour une origine existante
   */
  async function updateOrigin(id: string, updates: UpdateOriginDto): Promise<boolean> {
    try {
      const { error } = await supabase.from('origins').update(updates).eq('id', id);

      if (error) throw error;

      setOrigins((prev) =>
        prev.map((origin) => (origin.id === id ? { ...origin, ...updates } : origin))
      );
      toast.success('Origine mise à jour');
      return true;
    } catch (error) {
      console.error('Error updating origin:', error);
      toast.error('Erreur lors de la mise à jour');
      return false;
    }
  }

  /**
   * Désactive une origine (soft delete)
   */
  async function deactivateOrigin(id: string): Promise<boolean> {
    return updateOrigin(id, { is_active: false });
  }

  useEffect(() => {
    fetchOrigins();
  }, [fetchOrigins]);

  return {
    origins,
    loading,
    createOrigin,
    updateOrigin,
    deactivateOrigin,
    refetch: fetchOrigins,
  };
}
